import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { toast } from "react-toastify";
import RichTextToolbar from '@/features/profile/templates/ProfileRichTextToolbar'
import { selectUserData } from "@/api/rtk/userSelectors"
import { useTranslation } from "react-i18next";
import { useUpdateTemplateMutation } from "@/api/rtk/mutationsApi"
import { safeParseToHTML } from "@/features/profile/templates/templateEditorHtml"
import { TemplateEditorToolbar } from "@/features/profile/templates/TemplateEditorToolbar"
import { TemplateEditorMetaFields } from "@/features/profile/templates/TemplateEditorMetaFields"

export default function TemplateEditor({
  template,
  tools,
  onBack,
  onSaved,
}) {
  const { t } = useTranslation();
  const user = useSelector(selectUserData);
  const [updateTemplateMutation] = useUpdateTemplateMutation();
  const ownershipOptions = useMemo(
    () => [
      { name: t("profile.templates.tabs.personal"), value: "user" },
      { name: t("profile.templates.tabs.organisation"), value: "org" },
    ],
    [t]
  );

  const isOrgTemplate = template?.owner_type === "org";
  const isAdmin = Boolean(user?.is_admin);
  const isReadOnly = isOrgTemplate && !isAdmin;

  const [name, setName] = useState(template?.name || "");
  const [toolId, setToolId] = useState(template?.tool_id || "");
  const [ownerType, setOwnerType] = useState(template?.owner_type || "user");
  const [editorJson, setEditorJson] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(() => ({
    name: template?.name || "",
    toolId: template?.tool_id || "",
    ownerType: template?.owner_type || "user",
    json: null,
  }));

  useEffect(() => {
    setName(template?.name || "");
    setToolId(template?.tool_id || "");
    setOwnerType(template?.owner_type || "user");
    setIsSaving(false);
    setIsDeleting(false);
    setInitialSnapshot({
      name: template?.name || "",
      toolId: template?.tool_id || "",
      ownerType: template?.owner_type || "user",
      json: null,
    });
  }, [template?.id]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
    ],
    content: template?.value_json != null ? template.value_json : safeParseToHTML(template?.value || ""),
    editable: !isReadOnly,
    editorProps: {
      attributes: {
        class:
          "min-h-[420px] w-full rounded-xl border border-[#E4E4E7] bg-white p-4 text-sm leading-6 focus:outline-none whitespace-pre-wrap",
        spellcheck: "false",
        autocorrect: "off",
        autocapitalize: "off",
      },
    },
    onUpdate: ({ editor: ed }) => {
      setEditorJson(ed.getJSON());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(
      template?.value_json != null ? template.value_json : safeParseToHTML(template?.value || "")
    );
    const json = editor.getJSON();
    setEditorJson(json);
    setInitialSnapshot((prev) => ({ ...prev, json }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, template?.id]);

  const currentJsonKey = useMemo(() => JSON.stringify(editorJson ?? editor?.getJSON?.() ?? null), [editorJson, editor]);

  const isDirty =
    name !== initialSnapshot.name ||
    toolId !== initialSnapshot.toolId ||
    ownerType !== initialSnapshot.ownerType ||
    currentJsonKey !== JSON.stringify(initialSnapshot.json);

  const handleSave = async () => {
    try {
      if (isReadOnly) return;
      if (isSaving || isDeleting) return;
      setIsSaving(true);
      const json = editor?.getJSON?.() ?? editorJson ?? null;

      const nameChanged = name !== initialSnapshot.name;
      const toolChanged = toolId !== initialSnapshot.toolId;
      const jsonChanged = currentJsonKey !== JSON.stringify(initialSnapshot.json);
      const ownerChanged = ownerType !== initialSnapshot.ownerType;

      const patch = {};
      if (nameChanged) patch.name = name;
      if (toolChanged) patch.tool_id = toolId || null;
      if (jsonChanged) patch.value_json = json;

      if (user?.is_admin && ownerChanged) {
        patch.owner_type = ownerType;
      }

      if (!Object.keys(patch).length) {
        toast.info("No changes to save");
        return;
      }

      await updateTemplateMutation({
        id: template?.id,
        action: "update",
        data: patch,
      }).unwrap();

      setInitialSnapshot({ name, toolId, ownerType, json });
      toast.success("Template saved");

      if (ownerChanged) {
        onSaved?.({ owner_type: ownerType });
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to save template");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (isReadOnly) return;
      if (isSaving || isDeleting) return;
      setIsDeleting(true);
      await updateTemplateMutation({ id: template?.id, action: "delete" }).unwrap();
      toast.success("Template deleted");
      onBack?.();
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete template");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="w-full">
      <TemplateEditorToolbar
        onBack={onBack}
        isReadOnly={isReadOnly}
        isDirty={isDirty}
        isSaving={isSaving}
        isDeleting={isDeleting}
        onSave={handleSave}
        onDelete={handleDelete}
      />

      <TemplateEditorMetaFields
        templateId={template?.id}
        name={name}
        setName={setName}
        toolId={toolId}
        setToolId={setToolId}
        ownerType={ownerType}
        setOwnerType={setOwnerType}
        tools={tools}
        ownershipOptions={ownershipOptions}
        isAdmin={isAdmin}
        isReadOnly={isReadOnly}
      />

      <div className="rounded-2xl bg-white">
        <div className="flex flex-col gap-3">
          {!isReadOnly ? <RichTextToolbar editor={editor} /> : null}
          <EditorContent editor={editor} className="overflow-y-auto max-h-[460px] no-scrollbar"/>
        </div>
      </div>
    </div>
  );
}
